// Service to handle OneSignal player ID retrieval and database updates
import { supabase } from '@/integrations/supabase/client';

export const getOneSignalPlayerId = async (): Promise<string | null> => {
  try {
    if (!(window as any).OneSignal) {
      console.log('OneSignal not available for player ID retrieval');
      return null;
    }

    // Wait a bit for OneSignal to be fully ready
    await new Promise(resolve => setTimeout(resolve, 2000));

    const OneSignal = (window as any).OneSignal;
    
    // Try multiple ways to get the player ID using updated SDK methods
    let playerId = null;
    
    try {
      const pushSubscription = OneSignal.User.PushSubscription;
      
      // Method 1: Direct ID access
      if (pushSubscription && pushSubscription.id) {
        playerId = pushSubscription.id;
        console.log('Player ID retrieved via direct access:', playerId);
        return playerId;
      }
      
      // Method 2: Check if subscription is available
      const subscription = await pushSubscription;
      if (subscription && subscription.id) {
        playerId = subscription.id;
        console.log('Player ID retrieved via subscription object:', playerId);
        return playerId;
      }
      
      // Method 3: Try alternative property names
      if (subscription && subscription.onesignalId) {
        playerId = subscription.onesignalId;
        console.log('Player ID retrieved via onesignalId:', playerId);
        return playerId;
      }
      
    } catch (error) {
      console.log('Could not get player ID via primary methods:', error);
    }

    // Method 4: Check if user is subscribed first, then get ID
    try {
      const isSubscribed = await OneSignal.User.PushSubscription.optedIn;
      if (isSubscribed) {
        const subAfterCheck = OneSignal.User.PushSubscription;
        if (subAfterCheck && subAfterCheck.id) {
          playerId = subAfterCheck.id;
          console.log('Player ID retrieved after subscription check:', playerId);
          return playerId;
        }
      } else {
        console.log('User is not subscribed to push notifications');
      }
    } catch (error) {
      console.log('Could not get player ID after subscription check:', error);
    }

    console.log('Could not retrieve player ID through any method');
    return null;
  } catch (error) {
    console.error('Error getting OneSignal player ID:', error);
    return null;
  }
};

export const savePlayerIdToProfile = async (userId: string, playerId: string): Promise<boolean> => {
  try {
    console.log('Saving player ID to profile:', { userId, playerId });
    
    const { error } = await supabase
      .from('profiles')
      .update({ onesignal_player_id: playerId })
      .eq('id', userId);
    
    if (error) {
      console.error('Error updating profile with player ID:', error);
      return false;
    }
    
    console.log('Successfully updated profile with player ID');
    return true;
  } catch (error) {
    console.error('Error saving player ID to profile:', error);
    return false;
  }
};

export const updatePlayerIdAfterPermission = async (userId: string): Promise<void> => {
  try {
    console.log('Attempting to retrieve and save player ID after permission grant...');
    
    // Try multiple times with increasing delays
    const maxAttempts = 5;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`Player ID retrieval attempt ${attempt}/${maxAttempts}`);
      
      const playerId = await getOneSignalPlayerId();
      
      if (playerId) {
        const success = await savePlayerIdToProfile(userId, playerId);
        if (success) {
          console.log('Player ID successfully retrieved and saved on attempt', attempt);
          return;
        }
      }
      
      // Wait longer between each attempt
      if (attempt < maxAttempts) {
        const delay = attempt * 2000; // 2s, 4s, 6s, 8s
        console.log(`Waiting ${delay}ms before next attempt...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    console.warn('Failed to retrieve player ID after all attempts');
  } catch (error) {
    console.error('Error in updatePlayerIdAfterPermission:', error);
  }
};